// Seller, Webhooks (`spec/24-croqui-alinhamento.md` §5, WEB-108). Zenith: igual ao frame
// `croqui-zenith/screens/seller/webhooks.tsx` em 1440.
//
// Foco da tela: a TABELA de endpoints. Quem tem `useQuery`/`useMutation` é esta rota; os
// componentes de `features/webhooks` recebem dado por prop.
//
// O detalhe abre em drawer COM URL (`?id=`), não em `RouteDrawer` com rota `$id`: a rota
// `$id` e a rota de layout não estão em `files_allowed` desta task (mesma decisão de
// WEB-57, "Questões abertas"). Deep link e botão de voltar continuam valendo; o
// `RouteDrawer` (WEB-101) é usado como popup CONTROLADO, sem `Outlet` embaixo (mesmo
// padrão de `features/withdrawals/withdrawal-form.tsx`, WEB-105).

import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Webhook } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import {
  createEndpoint,
  deleteEndpoint,
  deliveriesListOptions,
  ENDPOINTS_QUERY_KEY,
  endpointsQueryOptions,
  patchEndpoint,
  pingEndpoint,
  rotateEndpointSecret,
  rotatePostbackSecret,
  type WebhookEndpoint,
} from '../api/queries/webhooks.ts';
import { useMode } from '../app/mode.tsx';
import { Notice } from '../components/app/notice.tsx';
import { PageHeader } from '../components/app/page-header.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import {
  EndpointDetailDrawer,
  EndpointFormDrawer,
  type EndpointFormValue,
} from '../features/webhooks/endpoint-drawers.tsx';
import { EndpointsTable } from '../features/webhooks/endpoints-table.tsx';
import { endpointStatsOptions } from '../features/webhooks/local-queries.ts';
import {
  autoDisabledTitle,
  endpointErrorMessage,
  isAutoDisabled,
  isStepUpCancelled,
  MAX_ENDPOINTS,
  pingSentMessage,
} from '../features/webhooks/model.ts';

/** Quantas entregas do endpoint entram na linha do tempo do drawer (croqui: 3). */
const TIMELINE_SIZE = 3;

const searchSchema = z.object({
  /** Endpoint aberto no drawer. `?id=lixo` cai em `undefined` (`catch` do Zod). */
  id: z.string().min(1).optional().catch(undefined),
});

function bodyOf(value: EndpointFormValue) {
  return {
    url: value.url,
    events: value.events,
    ...(value.description === '' ? {} : { description: value.description }),
  };
}

function Webhooks() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { mode } = useMode();
  const queryClient = useQueryClient();

  const endpoints = useQuery(endpointsQueryOptions(mode));
  const rows = endpoints.data ?? [];

  // "Sucesso 7d" e "Último ping" (API-25) são por endpoint: uma chamada cada, com teto de
  // 5 por modo (`MAX_ENDPOINTS`).
  const stats = useQueries({
    queries: rows.map((endpoint) => endpointStatsOptions(mode, endpoint.id)),
    combine: (results) =>
      new Map(
        results.flatMap((result, index) => {
          const id = rows[index]?.id;
          return result.data === undefined || id === undefined ? [] : [[id, result.data] as const];
        }),
      ),
  });

  const selected = rows.find((endpoint) => endpoint.id === search.id);
  // Linha do tempo "Últimas entregas" do drawer: só busca com o drawer aberto.
  const deliveries = useInfiniteQuery({
    ...deliveriesListOptions(mode, { endpoint_id: search.id }),
    enabled: search.id !== undefined,
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<WebhookEndpoint | undefined>(undefined);
  const [createdSecret, setCreatedSecret] = useState<string | undefined>(undefined);
  const [rotated, setRotated] = useState<
    { secret: string; previousExpiresAt?: string } | undefined
  >(undefined);
  const [pingMessage, setPingMessage] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const openDetail = (endpoint: WebhookEndpoint | undefined): void => {
    setRotated(undefined);
    setPingMessage(undefined);
    // Sem isto o erro de uma ação no endpoint A continua em `actionError` e reaparece no
    // `ErrorState` do endpoint B.
    ping.reset();
    rotate.reset();
    reactivate.reset();
    remove.reset();
    void navigate({ search: { id: endpoint?.id }, replace: true });
  };

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [ENDPOINTS_QUERY_KEY] });
  };

  const save = useMutation({
    mutationFn: async (value: EndpointFormValue) => {
      const body = bodyOf(value);
      if (editing !== undefined) {
        // Descrição vazia no `PATCH` é `null` (limpar), não `''`: string vazia deixaria um
        // ` · ` órfão no detalhe.
        await patchEndpoint(mode, editing.id, {
          ...body,
          description: value.description === '' ? null : value.description,
        });
        return undefined;
      }
      return createEndpoint(mode, body);
    },
    onSuccess: async (created) => {
      if (created === undefined) {
        setFormOpen(false);
      } else {
        // O segredo aparece uma vez, no próprio drawer (croqui: `Card` "Segredo").
        setCreatedSecret(created.secret);
      }
      await refresh();
    },
  });

  const settled = {
    onSettled: async () => {
      setBusy(false);
      await refresh();
    },
  };

  const ping = useMutation({
    mutationFn: async (endpoint: WebhookEndpoint) => {
      setBusy(true);
      setPingMessage(undefined);
      return pingEndpoint(mode, endpoint.id);
    },
    onSuccess: (response) => {
      setPingMessage(pingSentMessage(response.delivery_id));
    },
    ...settled,
  });

  const rotate = useMutation({
    mutationFn: async (endpoint: WebhookEndpoint) => {
      setBusy(true);
      return rotateEndpointSecret(mode, endpoint.id);
    },
    onSuccess: (response) => {
      setRotated({
        secret: response.secret,
        previousExpiresAt: response.previous_secret_expires_at,
      });
    },
    ...settled,
  });

  /** `PATCH { enabled: true }` grava `disabled_reason = null` no servidor (API-25). */
  const reactivate = useMutation({
    mutationFn: async (endpoint: WebhookEndpoint) => {
      setBusy(true);
      return patchEndpoint(mode, endpoint.id, { enabled: true });
    },
    ...settled,
  });

  const remove = useMutation({
    mutationFn: async (endpoint: WebhookEndpoint) => {
      setBusy(true);
      return deleteEndpoint(mode, endpoint.id);
    },
    onSuccess: () => {
      openDetail(undefined);
    },
    ...settled,
  });

  const rotatePostback = useMutation({
    mutationFn: async () => rotatePostbackSecret(mode),
  });

  const autoDisabled = rows.filter(isAutoDisabled);
  const atLimit = rows.length >= MAX_ENDPOINTS;

  const openForm = (endpoint?: WebhookEndpoint): void => {
    save.reset();
    setCreatedSecret(undefined);
    setEditing(endpoint);
    setFormOpen(true);
  };

  const closeForm = (): void => {
    setFormOpen(false);
    setCreatedSecret(undefined);
  };

  const actionError = [ping.error, rotate.error, reactivate.error, remove.error].find(
    (error) => error !== null,
  );

  return (
    <>
      <PageHeader
        title="Webhooks"
        description="Endpoints que recebem os eventos da sua conta em tempo real"
      >
        <Button
          disabled={atLimit}
          onClick={() => {
            openForm();
          }}
        >
          <Webhook className="size-4" aria-hidden="true" />
          Novo endpoint
        </Button>
      </PageHeader>

      {autoDisabled.length > 0 && (
        <Notice
          tone="danger"
          title={autoDisabledTitle(autoDisabled.length)}
          sub="50 entregas seguidas falharam nos últimos 7 dias sem nenhum sucesso. Corrija o endpoint e reative manualmente."
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                openDetail(autoDisabled[0]);
              }}
            >
              Ver qual
            </Button>
          }
        />
      )}

      <EndpointsTable
        endpoints={rows}
        stats={stats}
        isLoading={endpoints.isPending}
        // Erro só quando não há lista: com o polling de 30 s, um flake num refetch de
        // fundo trocaria a lista carregada por uma tela de falha.
        error={endpoints.data === undefined ? endpoints.error : undefined}
        onRetry={() => {
          void endpoints.refetch();
        }}
        onSelect={openDetail}
        selectedId={search.id}
        onCreate={() => {
          openForm();
        }}
      />

      <div className="flex items-center justify-between gap-3 px-1 text-xs text-muted-foreground">
        <span>máx. {MAX_ENDPOINTS} endpoints por modo</span>
        <span className="hidden sm:inline">
          URL precisa ser https e pública, sem IP privado, localhost ou metadata da nuvem
        </span>
      </div>

      {/* Fora do croqui: o segredo do `postback_url` da cobrança (`spec/07` §4.2) só tem
          rotação nesta tela. O `inventario-seller.md` aloca o bloco em `seguranca.tsx`
          (WEB-65); enquanto ela não existe, tirar daqui deixaria a ação inalcançável.
          Marca #46 no croqui. */}
      <Card>
        <CardHeader>
          <CardTitle>Postback da loja</CardTitle>
          <CardDescription>
            O segredo que assina os postbacks enviados ao postback_url da cobrança
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-start gap-3">
          {rotatePostback.error !== null && !isStepUpCancelled(rotatePostback.error) && (
            <p role="alert" className="text-xs text-destructive">
              {endpointErrorMessage(rotatePostback.error)}
            </p>
          )}
          {rotatePostback.data !== undefined && (
            <p role="status" className="break-all font-mono text-xs text-success">
              {rotatePostback.data.secret}
            </p>
          )}
          <Button
            variant="secondary"
            size="sm"
            disabled={rotatePostback.isPending}
            onClick={() => {
              rotatePostback.mutate();
            }}
          >
            {rotatePostback.isPending ? 'Rotacionando…' : 'Rotacionar segredo do postback'}
          </Button>
        </CardContent>
      </Card>

      <EndpointFormDrawer
        open={formOpen}
        onClose={closeForm}
        endpoint={editing}
        isPending={save.isPending}
        error={save.error}
        createdSecret={createdSecret}
        onSubmit={(value) => {
          save.mutate(value);
        }}
      />

      <EndpointDetailDrawer
        endpoint={formOpen ? undefined : selected}
        stats={search.id === undefined ? undefined : stats.get(search.id)}
        deliveries={(deliveries.data?.pages[0]?.data ?? []).slice(0, TIMELINE_SIZE)}
        deliveriesLoading={deliveries.isPending && search.id !== undefined}
        onClose={() => {
          openDetail(undefined);
        }}
        onPing={(endpoint) => {
          ping.mutate(endpoint);
        }}
        onRotate={(endpoint) => {
          rotate.mutate(endpoint);
        }}
        onEdit={openForm}
        onDelete={(endpoint) => {
          remove.mutate(endpoint);
        }}
        onReactivate={(endpoint) => {
          reactivate.mutate(endpoint);
        }}
        busy={busy}
        message={pingMessage}
        error={actionError}
        rotatedSecret={rotated}
      />
    </>
  );
}

export const Route = createFileRoute('/app/developers/webhooks/')({
  validateSearch: searchSchema,
  component: Webhooks,
});
